import { useEffect, useRef, useState } from 'react';
import {
    getAppointment,
    getDMSAppointment,
    getDealersWithSameDMS,
    getDealerKeyFromDMS,
    getKiosk,
    IAppointment,
    ServiceComment,
    MaintenanceMenu,
    RNDService,
} from '../../../../API/GoMoto';
import { ServiceCategory, QRCodeType } from '../../../../API/Enums';
import {
    AppointmentDispatchTypes,
    useAppointmentDispatch,
    useAppointmentStateUnsafe,
    useAppointmentUpdateAndPersist,
    useClearAppointment,
} from '../../../../context/Appointment/appointment';
import { useNavigate, useLocation, NavigateFunction, Location } from 'react-router-dom';
import store from 'store2';
import { Types, useCartDispatch } from '../../../../context/cart';
import { useQRScanner } from '../../../../context/hardware';
import {
    useFullReset,
    useKioskDispatch,
    useKioskImage,
    useKioskState,
    useAdditionalDealers,
    useSwitchDealer,
    useKioskTimeTable,
} from '../../../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationDispatch, useLocalizationExtension } from '../../../../context/Localization/localization';
import { ScanErrorCode } from '../../../../context/mzero';
import { useHideKioskBackground } from '../../../../context/Page/page';
import { GenericClickable } from '../../../../controls/GenericClickable/GenericClickable';
import { Heading } from '../../../../controls/Heading/Heading';
import { KioskImageDisplay } from '../../../../controls/KioskImageDisplay';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { ScrollableBody } from '../../../Layout/ScrollableBody';
import { default as css } from './ScanQRCode.module.scss';
import { Appointment_KeydropRoutePaths, AppointmentRoutePaths, AppRoutePaths, PathName } from '../../../../helpers/Routes';
import { GMDialogInfo } from '../../../../controls/Dialog';
import { Dealer, Kiosk, KioskDispatchTypes } from '../../../../context/Kiosk/kiosk.types';
import { GMRoundedIcon, GMSizedIcon } from '../../../../controls/Icon';
import { CTAButton } from '../../../../controls/ButtonBase';
import { LocalizationDispatchTypes, LocalizationType } from '../../../../context/Localization/localization.types';
import { isDevelopment } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { useDevelopmentState } from '../../../../context/Development/Development';
import { GMButtonGroup } from '../../../../controls/ButtonGroup';
import { ScanQRCodeDevTools } from '../ScanQRCodeDevTools/ScanQRCodeDevTools';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { ScanError, ScanQRCodeProps } from './ScanQRCode.types';
import { ExtendedDevQRCodeData } from '../ScanQRCodeDevTools/ScanQRCodeDevTools.types';

// TODO: This is also effectively a common control and should be moved.

/**
 * Handles scanning and processing a customer QR code for a check-in to continue a saved session or for a pick-up appointment.
 *
 * @param validateAppointmentKey - {@link ScanQRCodeProps.validateAppointmentKey validateAppointmentKey} description.
 * @param onProceed - {@link ScanQRCodeProps.onProceed onProceed} description.
 * @param onResendClick - {@link ScanQRCodeProps.onResendClick onResendClick} description.
 * @param primaryHeading - {@link ScanQRCodeProps.primaryHeading primaryHeading} description.
 * @param secondaryHeading - {@link ScanQRCodeProps.secondaryHeading secondaryHeading} description.
 * @param headingIconType - {@link ScanQRCodeProps.headingIconType headingIconType} description.
 * @param iconAriaLabel - {@link ScanQRCodeProps.iconAriaLabel iconAriaLabel} description.
 */
export const ScanQRCode: GoMotoComponent<ScanQRCodeProps> = ({
    validateAppointmentKey,
    onProceed,
    onResendClick,
    primaryHeading,
    secondaryHeading,
    headingIconType,
    iconAriaLabel,
}) => {
    useHideKioskBackground()();
    const { key: kioskKey, dealer, kioskType } = useKioskState();
    const additionalDealers: Dealer[] = useAdditionalDealers();
    const kioskDispatch = useKioskDispatch();
    const switchDealer = useSwitchDealer();
    const {
        result,
        inProgress,
        setResult,
        start,
        error: scanError,
        rawResult,
    } = useQRScanner();
    const Localization: LocalizationType = useLocalization();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const setAppointment = useAppointmentUpdateAndPersist();
    const reset: VoidFunction = useFullReset();
    const clearAppointment = useClearAppointment();
    const cartDispatch = useCartDispatch();
    const { formatAsString } = useLocalizationExtension();
    const scanQRCodeKioskImage = useKioskImage('ScanQRCode');
    const appointmentDispatch = useAppointmentDispatch();
    const localeDispatch = useLocalizationDispatch();
    const navigate: NavigateFunction = useNavigate();
    const location: Location<unknown> = useLocation();
    const initialInteraction = useRef<Date>(new Date());
    const { getIsKioskActuallyClosed, computeNextAvailableCheckinTime, getIsAppointmentTimeAfterOffset } = useKioskTimeTable();
    const { isDev } = useDevelopmentState();

    const [error, setError] = useState<ScanError>(ScanError.none);
    const [devData, setDevData] = useState<Pick<ExtendedDevQRCodeData, 'total' | 'paid'> | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [appointmentKiosk, setAppointmentKiosk] = useState<Kiosk | undefined>(undefined);
    const [isDevToolsOpen, setIsDevToolsOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!result && !scanError && !rawResult) {
            start();
        }
    }, [result, rawResult, scanError, start]);

    useEffect(() => {
        setResult && store.session.has('tempResult') && setResult(store.session.remove('tempResult'));
    }, [setResult]);

    useEffect(() => {
        if (scanError) {
            switch (scanError.code) {
                case ScanErrorCode.scannerNotFound:
                    setError(ScanError.ScannerNotFound);
                    break;
                default:
                    setError(ScanError.BadFormat);
                    break;
            }
        }
    }, [scanError]);

    useEffect(() => {
        (async () => {
            if (result && !loading && !appointment && !error) {
                setLoading(true);

                appointmentDispatch({
                    type: AppointmentDispatchTypes.MaskField,
                    payload: 'all',
                });
                !result.maskPhone && appointmentDispatch({
                    type: AppointmentDispatchTypes.UnmaskField,
                    payload: 'phone',
                });
                !result.maskEmail && appointmentDispatch({
                    type: AppointmentDispatchTypes.UnmaskField,
                    payload: 'email',
                });
                !result.maskVin && appointmentDispatch({
                    type: AppointmentDispatchTypes.UnmaskField,
                    payload: 'vin',
                });

                if (result.key) {
                    // non-DMS appointment
                    getAppointment(result.key)
                        .then((appointment) => {
                            if (appointment) {
                                const apptDealerKey = appointment.dealerKey ? appointment.dealerKey : result.dealerKey;
                                const apptDealer = additionalDealers.find((i: Dealer) => i.key === apptDealerKey);
                                if (apptDealer && apptDealer.key !== dealer.key) {
                                    switchDealer(apptDealer);
                                }
                                else if (!apptDealer) {
                                    // appointment exists, but not at any dealer for this kiosk
                                    setError(ScanError.dealerDoesNotHaveAppointment);

                                    return;
                                }

                                appointment.dealerKey = apptDealer.key;
                                appointment.phoneTypeEnum = result.phoneType ?? appointment.phoneTypeEnum;
                                if (devData) {
                                    appointment.serviceTotal = devData.total || appointment.serviceTotal;
                                    appointment.paymentStatus = devData.paid || appointment.paymentStatus;
                                }

                                if (getIsKioskActuallyClosed() && getIsAppointmentTimeAfterOffset(appointment.appointmentDateTime) && !appointment.storedByAdvisor) {
                                    appointment.appointmentDateTime = computeNextAvailableCheckinTime()
                                        ?.toDate() ?? appointment.appointmentDateTime;
                                }

                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.Update,
                                    payload: appointment,
                                });
                                appointmentDispatch({ type: AppointmentDispatchTypes.DeserializePreCheckIn });
                                localeDispatch({
                                    type: LocalizationDispatchTypes.SetLocale,
                                    payload: appointment.locale,
                                });
                            }
                            else {
                                // appointment does not exist
                                setError(ScanError.appointmentNotFound);

                                return;
                            }
                        })
                        .catch(() => {
                            setError(ScanError.appointmentNotFound);
                        })
                        .finally(() => setLoading(false));
                }
                else if (result.apptNo !== undefined && !store.session.has('tempAppointment')) {
                    /*
                     * use the result's dealer key, the dealer key from DMS details (if available), and the kiosk's currently displayed dealer key in that order
                     * if QR code had DMS details but could not get a dealer key from the server, inform the user that we do not have this DMS appointment
                     */
                    const dealerKey: string | null =
                        result.dealerKey ||
                        (result.dmsDealerKey ? await getDealerKeyFromDMS(kioskKey, result.dmsDealerKey, result.dmsStoreId, result.dmsAreaId) : dealer.key);
                    if (!dealerKey) {
                        setError(ScanError.dealerDoesNotHaveAppointment);
                        setLoading(false);

                        return;
                    }

                    getDMSAppointment({
                        kioskKey,
                        dealerKey,
                        appointmentNumber: result.apptNo,
                    })
                        .then(async (dmsAppointment) => [dmsAppointment, (await getDealersWithSameDMS(kioskKey, dealerKey)) || []])
                        .then((_) => {
                            const [dmsAppointment, dealerKeys] = [_[0] as IAppointment | null, _[1] as Array<string>];

                            if (dmsAppointment) {
                                if (dealerKeys.length === 0) {
                                    setError(ScanError.dealerDoesNotHaveAppointment);

                                    return;
                                }
                                else if (dealerKeys.length > 1) {
                                    kioskDispatch({
                                        type: KioskDispatchTypes.SetPossibleDealers,
                                        payload: dealerKeys,
                                    });
                                    dmsAppointment.dealerKey = '';
                                }
                                else {
                                    // only one dealer key found; no need to disambiguate
                                    const apptDealer = additionalDealers.find((i: Dealer) => i.key === dealerKeys[0]);
                                    if (apptDealer && apptDealer.key !== dealer.key) {
                                        switchDealer(apptDealer);
                                    }
                                    else if (!apptDealer) {
                                        // dealer key not available at this kiosk
                                        setError(ScanError.dealerDoesNotHaveAppointment);

                                        return;
                                    }
                                }

                                dmsAppointment.phoneTypeEnum = result.phoneType ?? dmsAppointment.phoneTypeEnum;
                                const { key: dmsAppointmentKey } = dmsAppointment;
                                if (devData) {
                                    dmsAppointment.serviceTotal = devData.total || dmsAppointment.serviceTotal;
                                    dmsAppointment.paymentStatus = devData.paid || dmsAppointment.paymentStatus;
                                }

                                const newAppointment: IAppointment = {
                                    ...dmsAppointment,
                                    key: '',
                                    dmsAppointmentKey,
                                    paymentStatus: dmsAppointment.paymentStatus || 'no payment due',
                                    dmsAppointmentId: dmsAppointment.id,
                                };

                                if (dmsAppointment.dealerKey) {
                                    appointmentDispatch({
                                        type: AppointmentDispatchTypes.HasAppointment,
                                        payload: true,
                                    });

                                    if (
                                        newAppointment.status === 'completed' ||
                                        (newAppointment.status === 'scheduled' && (newAppointment.storedByAdvisor ?? false) === false)
                                    ) {
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.Update,
                                            payload: newAppointment,
                                        });

                                        return newAppointment;
                                    }

                                    return setAppointment(newAppointment, true, false, false, true, initialInteraction.current);
                                }
                                else {
                                    store.session.set('tempResult', JSON.stringify(result));
                                    store.session.set('tempAppointment', newAppointment);
                                    navigate(AppRoutePaths.Landing + location.pathname);

                                    return;
                                }
                            }
                            else {
                                const apptDealer: Dealer | undefined = additionalDealers.find((dealer: Dealer) => dealerKeys.includes(dealer.key));

                                if (!apptDealer) {
                                    setError(ScanError.dealerDoesNotHaveAppointment);
                                }
                                else {
                                    setError(ScanError.appointmentNotFound);
                                }

                                return;
                            }
                        })
                        .then(() => setLoading(false));
                }
                else if (store.session.has('tempAppointment')) {
                    // load DMS appointment from session storage (following dealer disambiguation)
                    setAppointment(store.session.remove('tempAppointment'), true);
                }
            }
        })();
    }, [
        additionalDealers,
        appointment,
        appointmentDispatch,
        computeNextAvailableCheckinTime,
        dealer.key,
        devData,
        error,
        getIsAppointmentTimeAfterOffset,
        getIsKioskActuallyClosed,
        kioskDispatch,
        kioskKey,
        loading,
        localeDispatch,
        location.pathname,
        navigate,
        result,
        setAppointment,
        switchDealer,
    ]);

    useEffect(() => {
        // Having a result and an appointment means we attempted a scan and successfully found an appointment.
        if (result && appointment) {
            console.log('QR scan data', result);

            if (appointment.status === 'completed') {
                setError(ScanError.appointmentCompleted);
            }
            else if (appointment.status === 'scheduled' && (appointment.storedByAdvisor ?? false) === false) {
                if (kioskType === 'express' && !appointment.keySlot) {
                    navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Keydrop, Appointment_KeydropRoutePaths.Locker));
                }
                else {
                    setError(ScanError.appointmentScheduled);
                }
            }
            else {
                let hasError: boolean = false;
                if (appointment.qrCodeType === QRCodeType.towIn) {
                    appointmentDispatch({ type: AppointmentDispatchTypes.TowInProcess,
                        payload: true });
                    navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Keydrop, Appointment_KeydropRoutePaths.Locker));
                }
                else if (appointment.qrCodeType === QRCodeType.vehicleReady) {
                    if (!appointment.keySlot) {
                        setError(ScanError.keyNotLoaded);
                        hasError = true;
                    }
                    else if (appointment.dealerKey !== dealer.key) {
                        setError(ScanError.dealerDoesNotHaveAppointment);
                        hasError = true;
                    }
                    else if (appointment.kioskKey !== kioskKey) {
                        setError(ScanError.kioskDoesNotHavekey);
                        hasError = true;
                    }
                    else if (appointment.paymentStatus === 'not paid' && dealer.onlinePaymentsActive) {
                        setError(ScanError.notPaid);
                        hasError = true;
                    }
                }
                else {
                    if (validateAppointmentKey) {
                        setError(ScanError.thisIsNotAVehiclePickupQrCode);
                        hasError = true;
                    }
                    else if (appointment.dealerKey !== dealer.key) {
                        const newDealer: Dealer | undefined = additionalDealers.find((dealer: Dealer) => dealer.key === appointment.dealerKey);
                        if (newDealer) {
                            switchDealer(newDealer);
                        }
                        else {
                            setError(ScanError.dealerDoesNotHaveAppointment);
                            hasError = true;
                        }
                    }
                }
                if (!isDevelopment() && appointment.qrCodeType === QRCodeType.unknown) {
                    setError(ScanError.NotAValidQrCode);
                    hasError = true;
                }
                if (!hasError) {
                    appointment.servicesConfirmed?.forEach((service: ServiceComment) => {
                        const fullService = {
                            id: service.id,
                            name: service.name || '',
                            nameSpanish: service.nameAltLang || '',
                            description: service.description || '',
                            descriptionSpanish: service.descriptionAltLang || '',
                            price: service.price,
                            priceLow: service.priceLow,
                            priceHigh: service.priceHigh,
                            spgPricing: service.spgPricing,
                            category: ServiceCategory.All,
                            imagePath: '',
                            imageUrl: '',
                            isTransport: service.isTransport,
                            useSpgPricing: !!service.spgPricing,
                            comment: service.comment,
                        };
                        cartDispatch({
                            type: Types.AddService,
                            payload: fullService,
                        });
                    });

                    // TODO: Revise xtimeServices type so it is easier to use the non-array variant of the type and the array variant.
                    appointment.xtimeServices?.forEach((xTimeService) => {
                        cartDispatch({
                            type: Types.AddXtimeService,
                            payload: {
                                id: xTimeService.id,
                                goMotoId: xTimeService.goMotoId,
                                type: 'standard',
                                name: xTimeService.name,
                                opCode: xTimeService.opCode,
                                price: xTimeService.price,
                                isCommonRepair: xTimeService.isCommonRepair,
                                comment: xTimeService.comment,
                                description: xTimeService.description,
                            },
                        });
                    });

                    appointment.maintenanceMenusConfirmed?.forEach((maintenanceMenu: MaintenanceMenu) => {
                        cartDispatch({
                            type: Types.AddMaintMenu,
                            payload: maintenanceMenu,
                        });
                    });

                    appointment.rndServices?.forEach((rndService: RNDService) => {
                        cartDispatch({
                            type: Types.AddRNDService,
                            payload: rndService,
                        });
                    });

                    appointment.comment && cartDispatch({
                        type: Types.AddComment,
                        payload: {
                            key: 'Services',
                            comment: appointment.comment,
                        },
                    });
                    appointment.transportation &&
                        cartDispatch({
                            type: Types.SetTransportation,
                            payload: {
                                type: appointment.transportation.type,
                                subtype: appointment.transportation.subtype,
                            },
                        });
                    onProceed(result, appointment);
                }
            }
        }
        else if (!result && appointment) {
            clearAppointment();
        }
    }, [
        appointment,
        appointmentDispatch,
        cartDispatch,
        clearAppointment,
        dealer.key,
        kioskKey,
        onProceed,
        result,
        validateAppointmentKey,
        additionalDealers,
        kioskDispatch,
        switchDealer,
        dealer.onlinePaymentsActive,
        kioskType,
        navigate,
    ]);

    useEffect(() => {
        if (error === ScanError.kioskDoesNotHavekey && appointment && !loading && !appointmentKiosk) {
            setLoading(true);
            (async () => {
                setAppointmentKiosk(await getKiosk({ kioskKey: appointment.kioskKey }));
            })();
            setLoading(false);
        }
    }, [appointment, appointmentKiosk, error, loading]);

    const hint: JSX.Element = (
        <span>
            {Localization.LostYourQRCode}
            {' '}
            <span className={css.lostCode}>
                <GenericClickable onClick={onResendClick}>{Localization.ClickHere}</GenericClickable>
            </span>
        </span>
    );

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={primaryHeading}
                    secondaryHeading={secondaryHeading}
                    hint={hint}
                    roundedIcon={
                        <GMRoundedIcon themeType={'secondary'} roundedBackgroundSize={'large'}>
                            <GMSizedIcon
                                iconType={headingIconType}
                                iconSize={headingIconType === 'Key' ? 'large' : 'extraLarge'}
                                ariaLabel={iconAriaLabel}
                            />
                        </GMRoundedIcon>
                    }
                    showMustache
                />
            }
            proceedButtons={
                isDev ? (
                    <GMButtonGroup
                        buttons={[
                            <CTAButton
                                styleType={'primary'}
                                onClick={() => {
                                    if (!isDevToolsOpen) {
                                        setIsDevToolsOpen(true);
                                    }
                                }}
                                label={'Open Dev Tools'}
                                id={'Open Dev Tools'}
                                ariaLabel={'Click to open Dev Tools dialog'}
                                kioskOptions={{
                                    inlineOptions: {
                                        sizedIcon: (
                                            <GMSizedIcon
                                                iconType={'Menu'}
                                                iconSize={'small'}
                                                ariaLabel={'Menu icon'}
                                            />
                                        ),
                                    },
                                }}
                            />,
                        ]}
                    />
                ) : undefined
            }
        >
            {inProgress && <Spinner />}
            <KioskImageDisplay
                left={{
                    className: css.left,
                    image: scanQRCodeKioskImage,
                }}
                right={{
                    top: {
                        text: Localization.ScanQRCodeUsingTheHighlightedReader,
                    },
                    image: useKioskImage('PhoneQR'),
                    bottom: Localization.PositionTheQRCode,
                }}
            />
            {(isDev && setResult && (headingIconType === 'Key' || headingIconType === 'QR')) && (
                <ScanQRCodeDevTools
                    onScan={({ total, paid, apptNo, dealerKey, dmsDealerKey, dmsStoreId, dmsAreaId, mfaCode, ...result }) => {
                        setDevData({
                            total,
                            paid,
                        });

                        if (apptNo) {
                            if (dmsDealerKey) {
                                setResult(`${dmsDealerKey} | ${dmsStoreId} | ${dmsAreaId} | ${apptNo}`);
                            }
                            else {
                                setResult(` | | ${dealerKey} | ${apptNo}`);
                            }
                        }
                        else {
                            setResult(JSON.stringify(result));
                        }
                    }}
                    isDevToolsOpen={isDevToolsOpen}
                    setIsDevToolsOpen={setIsDevToolsOpen}
                    qrCodeType={headingIconType === 'Key' ? 'pickup' : 'checkin'}
                />
            )}
            <GMDialogInfo
                // TODO: Commonize the logic on all of these to reduce the need to redeclare effectively the same dialog
                ariaLabel={Localization.YourKeyHasNotBeenLoaded}
                ariaDescribedBy={''}
                title={Localization.YourKeyHasNotBeenLoaded}
                open={error === ScanError.keyNotLoaded && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.ThisDealerDoesNotHaveYourAppointment}
                ariaDescribedBy={''}
                title={Localization.ThisDealerDoesNotHaveYourAppointment}
                open={error === ScanError.dealerDoesNotHaveAppointment && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.AnErrorHasOccured}
                ariaDescribedBy={''}
                title={
                    appointmentKiosk?.otherKioskErrorMessage ||
                    (appointmentKiosk && formatAsString(Localization.ThisKioskDoestNotHaveYourKeyGoToOther, appointmentKiosk.name))
                }
                open={!!appointment && error === ScanError.kioskDoesNotHavekey && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.AppointmentCompleted}
                ariaDescribedBy={''}
                title={Localization.AppointmentCompleted}
                open={error === ScanError.appointmentCompleted && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.NotAValidQrCode}
                ariaDescribedBy={''}
                title={Localization.NotAValidQrCode}
                open={error === ScanError.NotAValidQrCode && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.ThisIsNotAVehiclePickupQrCode}
                ariaDescribedBy={''}
                title={Localization.ThisIsNotAVehiclePickupQrCode}
                open={error === ScanError.thisIsNotAVehiclePickupQrCode && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.AppointmentNotFound}
                ariaDescribedBy={''}
                title={Localization.AppointmentNotFound}
                open={error === ScanError.appointmentNotFound && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.QRCodeBadFormat}
                ariaDescribedBy={''}
                title={Localization.QRCodeBadFormat}
                open={error === ScanError.BadFormat && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.QRCodeScannerNotFound}
                ariaDescribedBy={''}
                title={Localization.QRCodeScannerNotFound}
                open={error === ScanError.ScannerNotFound && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.YourCheckInIsComplete}
                ariaDescribedBy={''}
                title={Localization.YourCheckInIsComplete}
                open={error === ScanError.appointmentScheduled && !loading}
                onClose={reset}
            />
            <GMDialogInfo
                ariaLabel={Localization.AppointmentNotPaid}
                ariaDescribedBy={''}
                title={Localization.AppointmentNotPaid}
                open={error === ScanError.notPaid && !loading}
                onClose={reset}
            />
        </ScrollableBody>
    );
};
